/** Build a parent School Guide pack from live school / admissions / catchment / ranking data. */

function shortName(name) {
  return String(name || "School")
    .replace(/^The\s+/i, "")
    .replace(/\s+School$/i, "")
    .trim();
}

function latestPolicy(policies) {
  const list = Array.isArray(policies) ? policies.filter((p) => Number(p.year_group) === 7 || p.year_group == null) : [];
  if (!list.length) return null;
  return list.slice().sort((a, b) => Number(b.entry_year || 0) - Number(a.entry_year || 0))[0];
}

function latestDefs(definitions) {
  const years = (definitions || [])
    .map((d) => Number(d.catchment_year))
    .filter((y) => Number.isFinite(y));
  if (!years.length) return [];
  const year = Math.max(...years);
  return (definitions || [])
    .filter((d) => Number(d.catchment_year) === year)
    .sort((a, b) => Number(a.catchment_priority || 99) - Number(b.catchment_priority || 99));
}

function drawableDefs(defs) {
  return defs.filter((d) => String(d.geography_type || "").toLowerCase() !== "open");
}

/** One band per catchment_priority from this school's latest defs only. Never copy another school. */
function uniquePriorityBands(defs) {
  const byPriority = new Map();
  drawableDefs(defs).forEach((def) => {
    const priority = Number(def.catchment_priority) || 1;
    const title = String(def.catchment_type_display || def.catchment_key || `Priority ${priority}`).trim();
    if (!byPriority.has(priority)) {
      byPriority.set(priority, {
        priority,
        title,
        detail: def.catchment_boundary_display || def.geography_type || "",
        geographyType: def.geography_type || "",
      });
      return;
    }
    const existing = byPriority.get(priority);
    if (title && !existing.title.split("; ").includes(title)) {
      existing.title = `${existing.title}; ${title}`;
    }
  });

  const bands = [...byPriority.values()].sort((a, b) => a.priority - b.priority);
  // Collapse identical titles (e.g. historic "Previous offer distance" rows).
  const collapsed = [];
  bands.forEach((band) => {
    const prev = collapsed[collapsed.length - 1];
    if (prev && prev.title === band.title) return;
    collapsed.push(band);
  });
  return collapsed;
}

function childNoun(school) {
  return genderLabel(school) === "Girls" ? "Girl" : "Boy";
}

function looksLikeInnerOuter(bands) {
  if (bands.length !== 2) return false;
  const blob = bands.map((b) => b.title.toLowerCase()).join(" ");
  return (
    (blob.includes("inner") && blob.includes("outer")) ||
    (/\barea a\b/.test(blob) && /\barea b\b/.test(blob)) ||
    (blob.includes("priority area 1") && blob.includes("priority area 2")) ||
    (blob.includes("zone 1") && blob.includes("zone 2"))
  );
}

function genderLabel(school) {
  const raw = String(school?.gender_type || school?.gender || "").toLowerCase();
  if (raw.includes("girl")) return "Girls";
  if (raw.includes("boy")) return "Boys";
  if (raw.includes("mixed") || raw.includes("co-ed")) return "Mixed";
  return raw ? raw[0].toUpperCase() + raw.slice(1) : "—";
}

function areaLabel(school) {
  return school?.local_authority || school?.council_name || school?.area || "—";
}

function catchmentHeroLabel(school, defs) {
  const bands = uniquePriorityBands(defs);
  const cat = String(school?.catchment_category || "").trim();
  if (!bands.length) {
    if (cat === "Open" || !cat) return "Open";
    return cat === "Designated" ? "Designated" : "Open";
  }
  if (bands.length === 1) return bands[0].title || (cat === "Designated" ? "Designated" : "Catchment");
  if (cat === "Both") return `${bands.length} bands + open`;
  return `${bands.length} bands`;
}

function examCluster(policy, school) {
  const blob = [
    policy?.test_authority,
    policy?.exam_assessment_type,
    school?.local_authority,
    school?.council_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  // Named consortia first (before loose acronym checks).
  if (blob.includes("medway")) return "medway";
  if (blob.includes("bexley")) return "bexley";
  if (blob.includes("west midlands")) return "west_midlands";
  if (blob.includes("trafford")) return "trafford";
  if (blob.includes("buckinghamshire") || blob.includes("secondary transfer")) return "bucks";
  if (blob.includes("csse") || (blob.includes("essex") && blob.includes("consortium"))) return "essex";
  if (blob.includes("lincolnshire")) return "lincs";
  if (blob.includes("gloucestershire")) return "glos";
  if (blob.includes("bournemouth") || blob.includes("christchurch") || blob.includes("poole") || /\bbcp\b/.test(blob)) {
    return "bcp";
  }
  if (blob.includes("wirral")) return "wirral";
  if (blob.includes("slough")) return "slough";
  if (blob.includes("warwickshire")) return "warwickshire";
  if (blob.includes("redbridge")) return "redbridge";
  if (blob.includes("kent test") || blob.includes("pese") || (/\bkent\b/.test(blob) && blob.includes("test"))) {
    return "kent";
  }
  // Sutton Selective Eligibility Test only — not "set by" / "set of" / St Olave’s own “SET”.
  if (
    blob.includes("selective eligibility") ||
    (blob.includes("sutton") && (/\bset\b/.test(blob) || blob.includes("selective")))
  ) {
    return "set";
  }
  if (policy?.admission_test) return "own";
  return "unknown";
}

function sharedExamParagraph(cluster) {
  const map = {
    kent:
      "A selective Kent Test result is the usual gateway to Kent’s grammar schools as a group — about thirty-plus schools use it for selective assessment. You do not sit a separate paper for each of them, but you still name only the schools you want on the CAF / SCAF, and each school applies its own catchment rules. Medway grammars often use a different test.",
    medway:
      "Medway’s selective test is shared across Medway grammar schools. A selective result can keep more than one Medway grammar in play, but you must still name each school on the CAF and each school ranks using its own oversubscription rules. Do not assume a Kent Test result covers Medway, or the other way round.",
    set:
      "One Selective Eligibility Test (SET) registration covers the joint Sutton selective group: Wilson’s, Sutton Grammar, Wallington County Grammar, Wallington High School for Girls, Nonsuch High School for Girls, and Greenshaw High School. Boys who pass are invited to the shared boys’ second stage; girls use the girls’ second-stage path. Greenshaw uses the SET without that second stage. You still name each school on the CAF — the test alone is not an application.",
    trafford:
      "Trafford’s GL consortium papers are shared across the Trafford grammar schools that use that consortium. One registration can keep several Trafford grammars in play, but each school still needs to be named on the CAF and applies its own qualifying score and catchment rules.",
    bucks:
      "Buckinghamshire’s Secondary Transfer Test is shared across Bucks grammar schools that use the county process. A qualifying result can keep more than one Bucks grammar in play; you still name preferences on the CAF and each school applies its own catchment and oversubscription rules.",
    essex:
      "Where a school uses the CSSE / Essex selective route, one sitting can keep several CSSE grammars in play. Confirm the current consortium list on the CSSE site, name each school on the CAF, and check each school’s own catchment rules.",
    lincs:
      "Lincolnshire’s consortium of grammar schools shares an entrance process. One registration can keep more than one Lincolnshire grammar in play, but you must still name each school on the CAF and each school applies its own catchment and oversubscription rules.",
    west_midlands:
      "The West Midlands Grammar Schools shared entrance test can keep several consortium grammars in play from one sitting. Confirm which schools are in the current consortium, name each on the CAF, and check each school’s own catchment rules.",
    glos:
      "Gloucestershire’s shared grammar entrance test can keep several Gloucestershire grammars in play from one sitting. You still name each school on the CAF; each school applies its own catchment and oversubscription rules.",
    bexley:
      "Bexley’s LA selection test is shared by the four Bexley grammar schools. A selective result can keep more than one of them in play, but you must still name each school on the CAF and each school applies its own oversubscription rules.",
    bcp:
      "The Bournemouth, Christchurch and Poole grammar consortium shares an entrance process. One registration can keep more than one BCP grammar in play; name each school on the CAF and check each school’s own catchment rules.",
    wirral:
      "Wirral’s consortium assessment is shared across Wirral grammar schools that use it. One sitting can keep more than one of those schools in play; you still name each on the CAF and each school applies its own rules.",
    slough:
      "The Slough Consortium of Grammar Schools shares an entrance test. One registration can keep several Slough-consortium grammars in play; name each school on the CAF and check each school’s catchment rules.",
    warwickshire:
      "Warwickshire’s shared grammar admissions process can keep more than one Warwickshire grammar in play from the same test route. Confirm the current list with Warwickshire Admissions, name each school on the CAF, and check each school’s own catchment rules.",
    redbridge:
      "Redbridge’s 11+ process can keep Redbridge grammar preferences in play from the same selective route. Confirm the current arrangements with the London Borough of Redbridge, name each school on the CAF, and check each school’s own rules.",
    own:
      "These entrance papers are for this school’s process. Sitting them does not automatically register your child for other grammars. If you also want other selective schools, complete their registrations and name them on the CAF as well.",
    unknown:
      "Check whether this school’s entrance test is shared with a local consortium or is school-only. Either way, eligibility after a test is not an offer — you must still name the school on the CAF / SCAF.",
  };
  return map[cluster] || map.unknown;
}

function timelineFromKeyDates(keyDates, entryYear) {
  if (!keyDates || typeof keyDates !== "object") {
    return [
      { when: "Year before Year 7", what: "Register for the entrance test (confirm dates on the school / LA site)" },
      { when: "31 October", what: "CAF / SCAF national closing date (confirm for your LA)" },
      { when: "1 March", what: "National Offer Day (confirm for your entry year)" },
    ];
  }
  const rows = [];
  const push = (key, label) => {
    if (keyDates[key]) rows.push({ when: String(keyDates[key]), what: label });
  };
  push("registration", "Test registration window");
  push("application_start", "Registration / applications open");
  push("registration_deadline", "Test registration closes");
  push("application_end", "Registration / applications close");
  push("stage1_exam", "Entrance test / Stage 1");
  push("stage1_result", "Test results / selective decision");
  push("results", "Test results / selective decision");
  push("stage2_exam", "Stage 2 exam (if used)");
  push("stage2_result", "Stage 2 results");
  push("caf_deadline", "CAF / SCAF deadline");
  push("offered_day", "National Offer Day");
  if (!rows.length && entryYear) {
    rows.push({ when: String(entryYear), what: "Confirm key dates on the school admissions page" });
  }
  return rows;
}

function placeOrderFromDefs(defs) {
  const seen = new Set();
  const out = [];
  // Prefer the first drawable def’s place_order (this school only).
  const preferred = drawableDefs(defs)[0] || defs[0];
  const list = Array.isArray(preferred?.style?.place_order) ? preferred.style.place_order : [];
  list.forEach((item) => {
    const title = typeof item === "string" ? item : item?.title || item?.label;
    if (!title || seen.has(title)) return;
    seen.add(title);
    out.push({
      title,
      detail:
        typeof item === "object" && item?.detail
          ? item.detail
          : "From this school’s published admission arrangements",
    });
  });
  if (!out.length) {
    uniquePriorityBands(defs).forEach((band) => {
      out.push({
        title: band.title,
        detail: band.detail || "Geography band on this school’s map",
      });
    });
  }
  if (!out.length) {
    out.push({
      title: "Eligible applicants",
      detail: "Follow this school’s current admission arrangements PDF — do not assume another school’s order",
    });
  }
  return out.slice(0, 10);
}

function buildCatchmentVisual(school, defs) {
  const bands = uniquePriorityBands(defs);
  const label = shortName(school.display_name || school.name);
  if (!bands.length) {
    return {
      kind: "open",
      aria: `${label} has no closed catchment ring`,
      caption: "",
      labels: {
        school: label,
        outside: "No closed catchment ring on the map for this school’s published geography.",
      },
    };
  }
  const layers = {};
  // Keep raw drawable defs for geometry layers (priority keys), but label from unique bands.
  drawableDefs(defs).forEach((def) => {
    const priority = Number(def.catchment_priority) || 1;
    if (layers[priority]) return;
    const band = bands.find((b) => b.priority === priority);
    layers[priority] = {
      title: band?.title || def.catchment_type_display || def.catchment_key || `Priority ${priority}`,
      detail: band?.detail || def.catchment_boundary_display || def.geography_type || "Mapped catchment band",
    };
  });
  const outside =
    bands.length === 1
      ? `Outside “${bands[0].title}” usually means lower priority once in-catchment places are filled — check this school’s PDF for whether out-of-area applicants can still be offered.`
      : "Outside the mapped priority bands for this school usually means lower priority once those bands are filled.";
  return {
    kind: "map",
    aria: `${label} catchment map`,
    caption: "",
    labels: {
      school: label,
      layers,
      outside,
    },
  };
}

/**
 * Example sketch + wording from THIS school's unique priority bands only.
 * Never invent an inner/outer story for a single-area school.
 */
function exampleFromDefs(defs, school) {
  const child = childNoun(school);
  const childLower = child.toLowerCase();
  const bands = uniquePriorityBands(defs);

  if (!bands.length) {
    return {
      title: "Example",
      kind: "open",
      keys: [
        { id: "B", label: "Nearer · lower score", tone: "muted" },
        { id: "A", label: "Farther · higher score · first", tone: "green" },
      ],
      body: `${child} A and ${childLower} B both pass and name this school. B lives much closer, but A has the higher score — so A is still offered first when this school ranks mainly by marks. Distance only separates equal ranks where the published rules say so.`,
    };
  }

  if (bands.length === 1) {
    const area = bands[0].title;
    return {
      title: "Example",
      kind: "single_area",
      keys: [
        { id: "A", label: `Inside · ${area}`, tone: "green" },
        { id: "B", label: `Outside · ${area}`, tone: "muted" },
      ],
      body: `${child} A lives inside this school’s “${area}”. ${child} B lives outside that same single area. After they both qualify, A is normally considered ahead of B under this school’s home-area rules. This school does not use a separate inner and outer band — only this one mapped area versus outside it. Inside the area, published rules such as pupil premium, siblings, or straight-line distance usually decide the order.`,
    };
  }

  if (bands.length === 2) {
    const [first, second] = bands;
    const kind = looksLikeInnerOuter(bands) ? "inner_outer" : "two_bands";
    return {
      title: "Example",
      kind,
      keys: [
        { id: "A", label: first.title, tone: "green" },
        { id: "B", label: second.title, tone: "blue" },
        { id: "C", label: "Outside both bands", tone: "muted" },
      ],
      body: `${child} A lives in “${first.title}”, ${childLower} B in “${second.title}”, and ${childLower} C outside both. After they qualify for this school, A is normally considered before B, and B before C, matching this school’s published band order. Inside one band, this school’s own rules (for example pupil premium or distance) decide the order — not another school’s policy.`,
    };
  }

  const first = bands[0];
  const second = bands[1];
  const rest = bands.slice(2).map((b) => b.title).join("; ");
  return {
    title: "Example",
    kind: "priority_layers",
    keys: [
      { id: "A", label: first.title, tone: "amber" },
      { id: "B", label: second.title, tone: "green" },
      { id: "C", label: rest || "Further / outside", tone: "blue" },
    ],
    body: `${child} A lives in “${first.title}”, ${childLower} B in “${second.title}”, and ${childLower} C in a further band${rest ? ` (${rest})` : ""} or outside. After selective status for this school, earlier bands are normally considered before later ones. Inside one band, follow this school’s published oversubscription rules only.`,
  };
}

function catchmentStepCopy(short, bands) {
  if (!bands.length) {
    return {
      summary: "Open / marks-led geography",
      paragraphs: [
        `${short} does not use a closed catchment ring that locks families in or out on the map. Eligible children can usually be considered from a wide area once they pass the entrance process.`,
        "What matters most is often marks order after the tests. Living closer only helps when this school’s published rules use distance as a tie-break — for example when two children have the same score.",
        `Use the permanent home address on the CAF / SCAF by the deadline for your entry year. That is the address used if distance or home-area rules apply.`,
      ],
      points: ["No closed catchment ring on the map for this school"],
    };
  }

  if (bands.length === 1) {
    const area = bands[0].title;
    return {
      summary: `One area: ${area}`,
      paragraphs: [
        `${short} uses a single mapped home area (“${area}”) after the entrance tests — not separate inner and outer bands.`,
        `Children who live inside “${area}” are normally considered ahead of those outside it, once they have met this school’s selective standard. Living outside does not always mean no chance: check this school’s PDF for out-of-area rules and distance.`,
        `Use the permanent home on the CAF / SCAF by the deadline for your entry year. That is the address checked against “${area}”.`,
      ],
      points: [area, "Outside that single area = usually lower priority", "Confirm the exact map on this school’s admissions pages"],
    };
  }

  const names = bands.map((b) => b.title);
  return {
    summary: `${bands.length} priority bands`,
    paragraphs: [
      `${short} uses ${bands.length} mapped priority bands after the entrance tests, in this school’s published order: ${names.join(" → ")}.`,
      "Passing the test makes your child eligible to be considered. It does not by itself guarantee a place if more eligible children want this school than there are places in their band.",
      `Use the permanent home on the CAF / SCAF by the deadline for your entry year. That is the address these bands are checked against.`,
    ],
    points: names.slice(0, 3).concat(["Outside mapped bands = usually lower priority"]).slice(0, 4),
  };
}

function gcseRankLabel(rankingRow, rankingList) {
  if (!rankingRow || rankingRow.attainment8 == null) return "—";
  const ranked = (rankingList || [])
    .filter((s) => s?.attainment8 != null)
    .slice()
    .sort((a, b) => Number(b.attainment8) - Number(a.attainment8));
  const idx = ranked.findIndex((s) => Number(s.id) === Number(rankingRow.id));
  return idx >= 0 ? `#${idx + 1}` : "—";
}

/**
 * @param {{ school: object, rankingRow?: object, rankingList?: object[], admissions?: object, catchments?: object }} input
 */
export function buildGeneratedJourney(input) {
  const school = input.school || {};
  const rankingRow = input.rankingRow || {};
  const policy = latestPolicy(input.admissions?.policies);
  const defs = latestDefs(input.catchments?.definitions);
  const name = school.display_name || school.name || policy?.school_name || "School";
  const short = shortName(name);
  const entryYear = policy?.entry_year;
  const intake = policy?.total_intake;
  const cluster = examCluster(policy, school);
  const bands = uniquePriorityBands(defs);
  const visual = buildCatchmentVisual(school, defs);
  const example = exampleFromDefs(defs, school);
  const placeOrder = placeOrderFromDefs(defs);
  const catchmentCopy = catchmentStepCopy(short, bands);
  const gender = genderLabel(school);

  const sources = [];
  if (policy?.exam_info_url) sources.push({ label: "Year 7 admissions / applying", href: policy.exam_info_url });
  if (policy?.policy_url) sources.push({ label: "Admission arrangements", href: policy.policy_url });
  if (school.website) {
    const href = String(school.website).startsWith("http") ? school.website : `https://${school.website}`;
    if (!sources.some((s) => s.href === href)) sources.push({ label: "School website", href });
  }
  if (!sources.length) {
    sources.push({ label: "Check your local authority admissions pages", href: "https://www.gov.uk/find-local-council" });
  }

  const timeline = timelineFromKeyDates(policy?.key_dates, entryYear);
  const examType = policy?.exam_assessment_type || "Published entrance assessment";
  const oxbridge =
    rankingRow.oxbridge != null
      ? String(rankingRow.oxbridge)
      : school.oxbridge_offers != null
        ? String(school.oxbridge_offers)
        : "—";

  const steps = [
    {
      id: "overview",
      title: "1. Overview",
      summary: `What ${short} is, in one minute`,
      paragraphs: [
        `${name} is a selective state grammar school${areaLabel(school) !== "—" ? ` in ${areaLabel(school)}` : ""}. It is not fee-paying. Children take an entrance process for a place in Year 7.`,
        intake
          ? `There are ${intake} Year 7 places in the latest published arrangements we hold for ${entryYear || "this school"}.`
          : "Check the school’s current admission arrangements for the published Year 7 intake.",
        policy?.test_authority
          ? `Entrance assessment is run under: ${policy.test_authority}.`
          : "Confirm the current entrance-test route on the school’s Year 7 admissions pages.",
      ],
    },
    {
      id: "catchment",
      title: "2. Catchment check",
      summary: catchmentCopy.summary,
      paragraphs: catchmentCopy.paragraphs,
      showCatchmentVisual: true,
      points: catchmentCopy.points,
      example,
    },
    {
      id: "apply",
      title: "3. How to apply",
      summary: entryYear ? `Key dates for ${entryYear} entry` : "Register, then name the school on the CAF",
      paragraphs: [
        entryYear
          ? `Dates below are from the school’s stored admissions record for Year 7 entry in ${entryYear}. Always re-check the school site if you are applying for a later year.`
          : "Confirm registration and CAF dates on the school and home local authority sites for your entry year.",
        "There are usually two steps: register for the entrance test (or consortium process), then name this school on your home local authority CAF / SCAF in true preference order. Missing either step usually means the school cannot offer a place.",
        sharedExamParagraph(cluster).includes("CAF")
          ? "Passing the test only creates eligibility. The school must still be named on the CAF / SCAF."
          : "Put the school in your true order of preference on the CAF / SCAF.",
      ],
      timeline,
    },
    {
      id: "exams",
      title: "4. Exams and preparation",
      summary: "What they sit, and what else one sitting can open",
      paragraphs: [
        examType,
        sharedExamParagraph(cluster),
        "Prepare with steady English and maths under timed conditions. Use the school or consortium familiarisation materials for the year you are entering, and gather access-arrangement evidence early if needed.",
      ],
      points: [
        policy?.test_authority || "Confirm the test authority on the school site",
        "Eligibility ≠ offer — still name the school on the CAF",
        "Re-check papers and dates each year",
      ],
    },
    {
      id: "place-order",
      title: "5. How places are ordered",
      summary: "The offer list, step by step",
      paragraphs: [
        `Once children are eligible, ${short} follows its own published place order rather than picking at random.`,
        "The list below is taken only from this school’s catchment / admissions data. Do not assume another school’s order. Always prefer the current PDF if anything differs.",
      ],
      placeOrder,
    },
    {
      id: "results",
      title: "6. Results",
      summary: "GCSE snapshot",
      paragraphs: [
        rankingRow.attainment8 != null
          ? `In the latest GCSE set we hold, Attainment 8 is ${rankingRow.attainment8}. That is one way to compare schools; it does not decide Year 7 offers.`
          : "GCSE Attainment 8 for this school is not in the ranking extract we hold yet — check DfE Compare school performance for the latest published figures.",
        oxbridge !== "—"
          ? `Oxbridge offers recorded here: ${oxbridge}. Treat that as a published school figure where available, not as a promise for any one child.`
          : "Oxbridge offer counts are not stored for this school in our extract.",
      ],
      facts: [
        { label: "GCSE rank", value: gcseRankLabel(rankingRow, input.rankingList) },
        { label: "Attainment 8", value: rankingRow.attainment8 != null ? String(rankingRow.attainment8) : "—" },
        { label: "Ofsted", value: rankingRow.ofsted || school.ofsted_rating || "—" },
      ],
    },
    {
      id: "admission",
      title: "7. Offer day",
      summary: "From the list to March",
      paragraphs: [
        "National Offer Day is at the start of March for Year 7. By then selective status and the school’s place order have already been applied.",
        `Re-check ${short}’s admission arrangements for the exact year you are entering — dates and small rules can move.`,
      ],
    },
  ];

  return {
    schoolId: Number(school.id),
    schoolName: name,
    kicker: "School guide",
    title: `What to do for ${short}`,
    lead: `A short parent guide: what the school is, how catchment works here, how to apply, exams, results, how places are ordered, and what to check before Offer Day.`,
    heroStats: [
      { label: "GCSE rank", value: gcseRankLabel(rankingRow, input.rankingList) },
      { label: "Gender", value: gender },
      { label: "Catchment", value: catchmentHeroLabel(school, defs) },
      { label: "Oxbridge offers", value: oxbridge },
      { label: "Area", value: areaLabel(school) },
    ],
    catchmentVisual: visual,
    sources,
    steps,
    generated: true,
  };
}
